import React, { useEffect, useState, useContext } from 'react'
import axios from 'axios'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBoxOpen, faMagnifyingGlass, faRecycle } from '@fortawesome/free-solid-svg-icons'
import { Link } from 'react-router-dom'
import DataTable from 'react-data-table-component'
import { Formik, Form, Field } from 'formik'
import CustomLoader from '../layouts/CustomLoader'
import CardProduct from './CardProduct'
import AuthContext from '../../middlewares/AuthContext'
import { useTranslation } from 'react-i18next'
import usePaginationComponentOptions from '../layouts/paginationComponentOptions'
import Swal from 'sweetalert2'

const url = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_URL
  : process.env.REACT_APP_URL_DEV

const BRAND      = '#6eb446'
const BRAND_DARK = '#2d5918'

interface AuthCtx {
  getHeaders: () => Promise<Record<string, string>>
}

interface Product { id: number; [key: string]: any }

const ProductsPlans: React.FC = () => {
  const { getHeaders }  = useContext(AuthContext) as AuthCtx
  const { t }           = useTranslation()
  const paginationComponentOptions = usePaginationComponentOptions()

  const [products, setProducts] = useState<Product[]>([])
  const [loading, setLoading]   = useState(false)
  const [totalRows, setTotalRows] = useState(0)
  const [perPage, setPerPage]   = useState(10)
  const [search, setSearch]     = useState('')

  const fetchProducts = async (page: number, perPageVal = perPage, searchVal = search) => {
    setLoading(true)
    try {
      const headers = await getHeaders()
      const res     = await axios.get<{ message: Product[]; total: number }>(
        `${url}products?page=${page}&per_page=${perPageVal}&search=${searchVal}`,
        { headers }
      )
      setProducts(res.data.message)
      setTotalRows(res.data.total)
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    } finally {
      setLoading(false)
    }
  }

  const handlePageChange    = (page: number) => fetchProducts(page)
  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPerPage(newPerPage)
    fetchProducts(page, newPerPage)
  }

  const handleSearch = (values: { search: string }) => {
    setSearch(values.search)
    fetchProducts(1, perPage, values.search)
  }

  useEffect(() => { fetchProducts(1) }, [])

  const columns = [
    {
      name: t('products.products'),
      cell: (row: Product, index: number) => (
        <CardProduct product={row as any} i={index} url={url} setProducts={setProducts as any} />
      ),
    },
  ]

  return (
    <div className="row align-items-center">
      <div className="col-sm-4 col-md-4 col-lg-2 mb-3">
        <Link to="products/create" 
         style={{
            display       : 'inline-flex',
            alignItems    : 'center',
            gap           : '0.45rem',
            padding       : '0.5rem 1.25rem',
            borderRadius  : 9999,
            background    : `linear-gradient(135deg, ${BRAND_DARK}, ${BRAND})`,
            color         : '#fff',
            fontWeight    : 700,
            fontSize      : '0.875rem',
            textDecoration: 'none',
            boxShadow     : `0 4px 14px ${BRAND}55`,
          }}
        >
          <FontAwesomeIcon icon={faBoxOpen} className="me-2" />
          <span className="text-capitalize fw-bold">{t('products.add_product')}</span>
        </Link>
      </div>

      <div className="col-sm-8 col-md-8 col-lg-10 mb-3">
        <Formik initialValues={{ search }} onSubmit={handleSearch} enableReinitialize>
          <Form>
            <div className="input-group">
              <Field
                type="text"
                className="form-control"
                placeholder={t('products.search')}
                name="search"
              />
              <button className="btn fw-bold" type="submit" style={{ backgroundColor: BRAND, color: '#fff', border: 'none' }}>
                <FontAwesomeIcon icon={faMagnifyingGlass} />
              </button>
            </div>
          </Form>
        </Formik>
      </div>

      <div className="col-md-12 mb-5">
        <div className="card card-shadow">
          <DataTable
            columns={columns}
            data={products}
            fixedHeader
            fixedHeaderScrollHeight="1000px"
            progressPending={loading}
            pagination
            paginationPerPage={perPage}
            paginationServer
            paginationTotalRows={totalRows}
            onChangeRowsPerPage={handlePerRowsChange}
            onChangePage={handlePageChange}
            paginationComponentOptions={paginationComponentOptions}
            paginationRowsPerPageOptions={[10, 20, 50, 100]}
            progressComponent={<CustomLoader />}
            dense
          />
          <div className="card-footer">
            <div className="row">
              <div className="col-12 col-sm-6 col-md-4 col-lg-3">
                <Link to="products/restore" className="btn btn-sm btn-shadow btn-warning opacity-50 text-dark mt-3 w-100">
                  <FontAwesomeIcon icon={faRecycle} />
                  <span className="ms-2">{t('products.plans_archived_txt')}</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProductsPlans
